/**
 * Dettaglio completo di un titolo dentro un portafoglio.
 *
 * È la "schermata piena" che apre la vista ridotta della tabella: posizione
 * attuale, costo di carico, andamento del valore della posizione e **tutti** i
 * movimenti, non solo gli ultimi.
 *
 * Regge anche il caso di una posizione chiusa: lo strumento non è più tra le
 * holding, ma è proprio allora che lo storico dei movimenti serve davvero.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import {
  INSTRUMENT_TYPE_LABELS,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatQuantity,
  labelFor,
  signOf,
} from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Spinner, StatCard, TableWrap } from '@/ui';
import { ValueTrendChart } from '@/ui/ValueTrendChart';
import { WatchlistToggleButton } from '@/features/watchlist/WatchlistToggleButton';
import { CryptoTradeButton } from './CryptoTradeButton';
import { ManualOrderButton } from './ManualOrderButton';
import { TransactionForm } from './TransactionForm';
import { TxTypeBadge } from './TxTypeBadge';
import { useDeleteTransaction, usePortfolioInstrument, usePortfolioTransactions } from './api';
import './portfolios.css';
import { InstrumentLogo } from '@/features/instruments/InstrumentLogo';

const EMPTY = <span className="text-muted">—</span>;

/** Quanti movimenti caricare: il dettaglio completo non è un elenco paginato. */
const TRANSACTIONS_LIMIT = 200;

function toneOf(value: string | null): 'positive' | 'negative' | 'neutral' {
  const sign = signOf(value);
  return sign === 'positive' ? 'positive' : sign === 'negative' ? 'negative' : 'neutral';
}

function toneClass(value: string | null): string {
  const sign = signOf(value);
  return sign === 'positive' ? 'text-positive' : sign === 'negative' ? 'text-negative' : '';
}

export function PortfolioInstrumentPage() {
  const { id = '', instrumentId = '' } = useParams<{ id: string; instrumentId: string }>();
  const [showForm, setShowForm] = useState(false);

  const detailQuery = usePortfolioInstrument(id, instrumentId);
  const transactionsQuery = usePortfolioTransactions(id, {
    instrumentId,
    limit: TRANSACTIONS_LIMIT,
  });
  const deleteTransaction = useDeleteTransaction(id);

  if (detailQuery.isPending) return <Spinner label="Caricamento del titolo…" />;

  if (detailQuery.isError) {
    const error = detailQuery.error;
    const notFound = error instanceof ApiError && error.status === 404;

    return (
      <div className="portfolios">
        <Alert variant="error" title={notFound ? 'Titolo non trovato' : 'Errore di caricamento'}>
          {notFound
            ? 'Questo titolo non risulta in questo portafoglio.'
            : error instanceof ApiError
              ? error.message
              : 'Errore imprevisto.'}
        </Alert>
        <Link className="btn btn--secondary" to={`/portfolios/${id}`}>
          Torna al portafoglio
        </Link>
      </div>
    );
  }

  const detail = detailQuery.data;
  const { instrument, currentPosition, history, positionHistory } = detail;
  const currency = currentPosition?.currency ?? detail.portfolio.baseCurrency;
  const transactions = transactionsQuery.data?.items ?? [];

  return (
    <div className="portfolios">
      <header className="portfolios__header">
        <div>
          <nav className="portfolios__breadcrumb">
            <Link to="/portfolios">Portafogli</Link> <span aria-hidden="true">›</span>{' '}
            <Link to={`/portfolios/${id}`}>{detail.portfolio.name}</Link>{' '}
            <span aria-hidden="true">›</span> <span>{instrument.name}</span>
          </nav>
          <h1 className="portfolios__title">
            <InstrumentLogo instrumentId={instrument.id} name={instrument.name} size="md" />
            {instrument.name}
          </h1>
          <p className="portfolios__subtitle">
            {instrument.isin ?? instrument.symbol ?? '—'} ·{' '}
            {labelFor(INSTRUMENT_TYPE_LABELS, instrument.instrumentType)}
            {currentPosition && ` · aggiornato al ${formatDate(currentPosition.snapshotDate)}`}
          </p>
        </div>
        <div className="portfolios__header-actions">
          <Button variant="secondary" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Chiudi' : 'Registra movimento'}
          </Button>
          {/* F-136: l'ordine manuale anche QUI, gia' puntato su questa posizione.
              Prima bisognava tornare alla pagina del portafoglio e riscegliere
              il titolo da un elenco — il passaggio piu' rischioso di tutta
              l'operazione, rifatto per niente. Parte in VENDITA perche' e' cio'
              che si fa da dentro una posizione; l'acquisto resta a un clic. */}
          {/* Su un portafoglio di un exchange crypto il comando e' un altro (SPEC-024 §9):
              coppie e saldi al posto di ticker e posizioni; l'asset e' gia' scelto. */}
          {instrument.instrumentType === 'crypto' && instrument.symbol && (
            <CryptoTradeButton portfolioId={id} fixedAsset={instrument.symbol} defaultSide="sell" />
          )}
          <ManualOrderButton
            portfolioId={id}
            // L'elenco resta vuoto: il titolo lo mette `fixedInstrument`, e qui
            // non serve altro — la scelta e' gia' fatta.
            holdings={[]}
            fixedInstrument={{
              id: instrument.id,
              name: instrument.name,
              symbol: instrument.symbol,
              // Senza, il modal diceva «ticker non associato» su una posizione
              // che il broker aveva appena sincronizzato (F-139).
              trading212Ticker: instrument.trading212Ticker,
            }}
            defaultSide="sell"
            label="Ordine sul titolo"
          />
          <WatchlistToggleButton instrumentId={instrument.id} instrumentName={instrument.name} />
        </div>
      </header>

      {showForm && (
        <Card title="Registra un movimento" subtitle="Anche se non hai tutti i dati: puoi lasciare vuoti i campi opzionali">
          <TransactionForm
            portfolioId={id}
            fixedInstrument={{ id: instrument.id, name: instrument.name }}
            defaultCurrency={currency}
            onSuccess={() => setShowForm(false)}
            onCancel={() => setShowForm(false)}
          />
        </Card>
      )}

      {currentPosition === null && (
        <Alert variant="info" title="Posizione chiusa">
          Questo titolo non compare nell&apos;ultimo snapshot del portafoglio: risulta venduto o
          trasferito. Lo storico dei movimenti qui sotto resta consultabile.
        </Alert>
      )}

      <section className="portfolios__stats">
        <StatCard
          label="Quantità"
          value={
            currentPosition
              ? formatQuantity(currentPosition.quantity)
              : history
                ? formatQuantity(history.netQuantity)
                : '—'
          }
          hint={currentPosition ? "Dall'ultimo snapshot" : 'Ricostruita dai movimenti'}
        />
        <StatCard
          label="Valore attuale"
          value={currentPosition ? formatCurrency(currentPosition.marketValue, currency) : '—'}
          hint={
            currentPosition?.currentPrice
              ? `Prezzo ${formatCurrency(currentPosition.currentPrice, currency)}`
              : 'Non disponibile'
          }
        />
        <StatCard
          label="Investito"
          value={history ? formatCurrency(history.investedEur, currency) : '—'}
          hint={history ? 'Costo medio ponderato' : 'Nessun movimento importato'}
        />
        <StatCard
          label="P&L non realizzata"
          value={
            currentPosition?.unrealizedPl
              ? formatCurrency(currentPosition.unrealizedPl, currency)
              : '—'
          }
          tone={toneOf(currentPosition?.unrealizedPl ?? null)}
          hint={
            currentPosition?.unrealizedPlPct
              ? formatPercent(currentPosition.unrealizedPlPct)
              : 'Richiede valore e costo'
          }
        />
      </section>

      {history && (
        <Card title="Riepilogo dai movimenti">
          <dl className="portfolios__facts">
            <dt>Primo acquisto</dt>
            <dd>{history.firstBuyDate ? formatDate(history.firstBuyDate) : '—'}</dd>
            <dt>Ultimo movimento</dt>
            <dd>{history.lastTransactionDate ? formatDate(history.lastTransactionDate) : '—'}</dd>
            <dt>Movimenti</dt>
            <dd className="numeric">{formatNumber(history.transactionsCount)}</dd>
            <dt>P&amp;L realizzata</dt>
            <dd className={`numeric ${toneClass(history.realizedPlEur)}`}>
              {formatCurrency(history.realizedPlEur, currency)}
            </dd>
            <dt>Dividendi incassati</dt>
            <dd className="numeric text-positive">
              {formatCurrency(history.dividendsReceivedEur, currency)}
            </dd>
            <dt>Commissioni</dt>
            <dd className="numeric">{formatCurrency(history.feesPaidEur, currency)}</dd>
            <dt>Imposte</dt>
            <dd className="numeric">{formatCurrency(history.taxesPaidEur, currency)}</dd>
          </dl>
        </Card>
      )}

      <Card title="Andamento della posizione" subtitle="Un punto per ogni snapshot importato">
        <ValueTrendChart
          label={`Andamento del valore di ${instrument.name}`}
          currency={currency}
          points={positionHistory.map((point) => ({
            date: point.snapshotDate,
            value: point.marketValue,
          }))}
        />
      </Card>

      <Card
        title="Storico movimenti"
        subtitle={
          transactionsQuery.data
            ? `${formatNumber(transactionsQuery.data.total)} movimenti su questo titolo`
            : undefined
        }
        flush
      >
        {transactionsQuery.isPending ? (
          <Spinner />
        ) : transactions.length === 0 ? (
          <EmptyState
            icon="📥"
            title="Nessun movimento"
            description="Importa la cronologia operazioni per vedere acquisti, vendite e dividendi di questo titolo."
          />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th className="numeric">Quantità</th>
                  <th className="numeric col--secondary">Prezzo</th>
                  <th className="numeric">Importo</th>
                  <th className="numeric col--tertiary">Commissioni</th>
                  <th className="numeric col--tertiary">Imposte</th>
                  <th className="col--secondary">Provenienza</th>
                  <th aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => {
                  const deletable = transaction.source === 'manual' && transaction.matchedAt === null;
                  return (
                    <tr key={transaction.id}>
                      <td className="cell--minor">{formatDateTime(transaction.txDate)}</td>
                      <td>
                        <TxTypeBadge txType={transaction.txType} />
                      </td>
                      <td className="numeric">
                        {transaction.quantity ? formatQuantity(transaction.quantity) : EMPTY}
                      </td>
                      <td className="numeric col--secondary">
                        {transaction.price
                          ? formatCurrency(transaction.price, transaction.currency)
                          : EMPTY}
                      </td>
                      <td className={`numeric ${toneClass(transaction.amount)}`}>
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </td>
                      <td className="numeric col--tertiary">
                        {formatCurrency(transaction.fees, transaction.currency)}
                      </td>
                      <td className="numeric col--tertiary">
                        {formatCurrency(transaction.taxes, transaction.currency)}
                      </td>
                      <td className="col--secondary">
                        <Badge tone={transaction.source === 'manual' ? 'accent' : 'neutral'}>
                          {transaction.source === 'manual' ? 'Manuale' : 'Import'}
                        </Badge>
                      </td>
                      <td>
                        {deletable && (
                          <Button
                            variant="ghost"
                            loading={deleteTransaction.isPending && deleteTransaction.variables === transaction.id}
                            onClick={() => deleteTransaction.mutate(transaction.id)}
                          >
                            Elimina
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
